import React, { useState, useEffect } from 'react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { DashboardApi } from '../../../apis/DashboardApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { shared } from "../../../reducer/SharedSlice";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { login } from '../../../reducer/loginSlice';
import SubscriptionStatusService from '../../../services/SubscriptionStatusService';
import { CreditCard, CheckCircle, AlertCircle } from 'lucide-react';

interface FormData {
  firstName: string;
  lastName: string;
  city: string;
  zip: string;
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  discountCode: string;
  plan_id: any;
  country_code: string;
  state: string;
  package_type: string;
}

interface PlanOption {
  id: number;
  title: string;
  price: string;
  yearly: string;
  recommended?: boolean;
  numUsers: string;
  storage: string;
  photoVideoStorage: string;
  securityEnhance: string;
}

const AddCard: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedPlanId = queryParams.get('plan') || '2';

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    city: '',
    zip: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    discountCode: '',
    plan_id: parseInt(preselectedPlanId),
    country_code: '',
    state: '',
    package_type: 'monthly'
  });

  const planOptions: PlanOption[] = [
    {
      id: 1,
      title: 'Basic',
      price: 'Free',
      yearly: 'Free',
      numUsers: '1 Recipient',
      storage: '100MB',
      photoVideoStorage: 'Unlimited',
      securityEnhance: 'None',
    },
    {
      id: 2,
      title: 'Gold',
      price: '$9.95 / month',
      yearly: '$100 / year (16% off)',
      recommended: true,
      numUsers: '100 Recipients',
      storage: '1TB',
      photoVideoStorage: 'Unlimited',
      securityEnhance: 'Security Enhance',
    },
    {
      id: 3,
      title: 'Platinum',
      price: '$19.95 / month',
      yearly: '$175 / year (45% off)',
      numUsers: '1000 Recipients',
      storage: '2TB',
      photoVideoStorage: 'Unlimited',
      securityEnhance: 'Security Enhance',
    }
  ];

  const [countries, setCountries] = useState<any>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const isLifetime = userLoginData?.data?.user_plan?.is_lifetime === true;
  const isAccountSuspended = userLoginData?.data?.user_plan?.status === 'suspended';

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response: AxiosResponse<any> = await DashboardApi.getUserData();
      setCountries(response?.data?.data?.countries);
    } catch (error: any) {
      console.error('Error fetching user data:', error.response?.data || error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatCardNumber(e.target.value);
    setFormData({ ...formData, cardNumber: formattedValue });
  };

  const formatExpiryDate = (value: string) => {
    // Remove any non-digit characters
    let v = value.replace(/\D/g, '');
    
    // If we have more than 2 digits, format as MM/YY
    if (v.length > 2) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    return v;
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = (e.target.value);
    setFormData({ ...formData, expiryDate: formattedValue });
  };

  const validateExpiryDate = (value: string) => {
    // Accept MMYY, MM/YY, MM-YY (with flexibility for single-digit month)
    const regex = /^(0?[1-9]|1[0-2])(\/|-)?([0-9]{2}|[0-9]{4})$/;
    
    if (!regex.test(value.replace(/\s/g, ''))) {
      return false;
    }
    
    // Extract month and year
    const parts = value.replace(/\s/g, '').split(/\/|-/);
    let month: number;
    let year: number;
    
    if (parts.length === 1) {
      // Format is MMYY
      month = parseInt(value.substring(0, 2));
      year = parseInt(value.substring(2));
    } else {
      // Format is MM/YY or MM-YY
      month = parseInt(parts[0]);
      year = parseInt(parts[1]);
    }
    
    // Add 2000 if year is two digits
    if (year < 100) {
      year += 2000;
    }
    
    // Check if date is in the future
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
    const currentYear = currentDate.getFullYear();
    
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate expiry date before submitting
    // if (!validateExpiryDate(formData.expiryDate)) {
    //   toast.error('Please enter a valid expiration date in the format MM/YY, MM-YY, or MMYY');
    //   return;
    // }

    try {
      setLoader(true);
      const response: any = await DashboardApi.processPayment(formData);
      
      if (response?.data) {
        // Payment successful
        const mediaResponse:any = await DashboardApi.getMediaUsage();
        
        if (mediaResponse?.data) {
          dispatch(shared({ data: mediaResponse?.data?.data }));
          
          // Clear form data
          setFormData({
            firstName: '',
            lastName: '',
            city: '',
            zip: '',
            cardNumber: '',
            expiryDate: '',
            cvv: '',
            discountCode: '',
            plan_id: parseInt(preselectedPlanId),
            country_code: '',
            state: '',
            package_type: 'monthly'
          });
          
          setPaymentSuccessful(true);
          toast.success('Payment Successful');

          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
          // Update account status if it was suspended
          if (isAccountSuspended) {
            // Update Redux state to mark account as active
            SubscriptionStatusService.getInstance().setAccountActive();
          }
        } else {
          toast.error('Error updating account after payment. Please contact support.');
        }
      } else {
        toast.error('Payment processing failed');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.payment?.responsetext || 'Payment failed');
    } finally {
      setLoader(false);
    }
  };

  // Find the selected plan
  const selectedPlan = planOptions.find(plan => plan.id === formData.plan_id) || planOptions[1]; // Default to Gold if not found

  if (isLifetime) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex flex-col md:flex-row">
        <Sidebar />
        <main className="flex-1 relative md:ml-64">
          <Navbar />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">
                {isAccountSuspended ? 'Renew Your Subscription' : 'Upgrade Your Account'}
              </h1>
              <p className="mt-2 text-gray-600">
                Choose a plan that's right for you and secure your digital legacy.
              </p>
            </div>
            
            {paymentSuccessful && (
              <div className="mb-8 bg-green-50 border border-green-200 p-4 rounded-lg shadow-sm">
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  <div>
                    <h3 className="text-green-800 font-medium text-lg">
                      {isAccountSuspended ? 'Subscription Successfully Renewed!' : 'Payment Successful!'}
                    </h3>
                    <p className="text-green-700">
                      {isAccountSuspended 
                        ? 'Your account is now active again. You\'ll be redirected shortly.' 
                        : 'Your plan has been upgraded. You\'ll be redirected to your dashboard shortly.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Plan Selection */}
              <div className="col-span-1">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                      Selected Plan
                    </h2>
                    
                    <div className="space-y-4">
                      {planOptions.filter(plan => plan.id !== 1).map((plan) => (
                        <div 
                          key={plan.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all relative ${
                            formData.plan_id === plan.id 
                              ? 'border-orange-500 bg-orange-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setFormData({...formData, plan_id: plan.id})}
                        >
                          {plan.recommended && (
                            <span className="absolute -top-3 right-3 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                              Recommended
                            </span>
                          )}
                          
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-gray-900">{plan.title}</h3>
                              <div className="mt-1">
                                <span className="text-xl font-bold text-gray-900">
                                  {formData.package_type === 'monthly' ? plan.price : plan.yearly}
                                </span>
                              </div>
                            </div>
                            
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              formData.plan_id === plan.id 
                                ? 'border-orange-500 bg-orange-500' 
                                : 'border-gray-300'
                            }`}>
                              {formData.plan_id === plan.id && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                          </div>
                          
                          <ul className="mt-4 space-y-2">
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                              <span className="text-gray-600 text-sm">{plan.numUsers}</span>
                            </li>
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                              <span className="text-gray-600 text-sm">{plan.storage} Storage</span>
                            </li>
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                              <span className="text-gray-600 text-sm">{plan.photoVideoStorage} Photo/Video Storage</span>
                            </li>
                            {plan.securityEnhance !== 'None' && (
                              <li className="flex items-start">
                                <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                                <span className="text-gray-600 text-sm">{plan.securityEnhance}</span>
                              </li>
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>
                    
                                          <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Billing Cycle
                      </label>
                      <div className="flex space-x-4">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, package_type: 'monthly'})}
                          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium ${
                            formData.package_type === 'monthly'
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Monthly
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, package_type: 'yearly'})}
                          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium ${
                            formData.package_type === 'yearly'
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Yearly
                          {/* {formData.plan_id === 2 && (
                            <span className="ml-1 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">Save 16%</span>
                          )}
                          {formData.plan_id === 3 && (
                            <span className="ml-1 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">Save 45%</span>
                          )} */}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Form */}
              <div className="col-span-1 lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      Payment Information
                    </h2>
                    <p className="text-gray-500 text-sm mb-6">
                      We accept Visa, Mastercard, and Discover cards only.
                    </p>
                    
                    <div className="flex justify-start mb-6">
                      <div className="flex space-x-2">
                        <img src="/images/visa.png" alt="Visa" className="h-8" />
                        <img src="/images/mastercard.jpg" alt="Mastercard" className="h-8" />
                        <img src="/images/discover.png" alt="Discover" className="h-8" />
                      </div>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                      {/* Card Information */}
                      <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                          <CreditCard className="mr-2 h-5 w-5 text-gray-500" />
                          Card Details
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
                              Card Number
                            </label>
                            <input
                              id="cardNumber"
                              name="cardNumber"
                              type="text"
                              placeholder="•••• •••• •••• ••••"
                              value={formData.cardNumber}
                              onChange={handleCardNumberChange}
                              maxLength={19}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Expiry Date
                              </label>
                              <input
                                id="expiryDate"
                                name="expiryDate"
                                type="text"
                                value={formData.expiryDate}
                                onChange={handleExpiryChange}
                                placeholder="MM/YY"
                                className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                maxLength={7} // Allow for MM/YYYY format
                                required
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Format: MM/YY, MM-YY, or MMYY
                              </p>
                            </div>
                            <div>
                              <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1">
                                CVV
                              </label>
                              <input
                                id="cvv"
                                name="cvv"
                                type="text"
                                placeholder="•••"
                                value={formData.cvv}
                                onChange={handleInputChange}
                                maxLength={4}
                                className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Billing Information */}
                      <div className="mb-6">
                        <h3 className="text-md font-medium text-gray-900 mb-4">
                          Billing Information
                        </h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                              First Name
                            </label>
                            <input
                              id="firstName"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required
                            />
                          </div>
                          <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                              Last Name
                            </label>
                            <input
                              id="lastName"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                              Country
                            </label>
                            <select
                              id="country_code"
                              name="country_code"
                              value={formData.country_code}
                              onChange={handleInputChange}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required
                            >
                              <option value="">Select Country</option>
                              {countries.map((data: any) => (
                                <option key={data.code} value={data.code}>
                                  {data.country_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                              State / Province
                            </label>
                            <input
                              id="state"
                              name="state"
                              value={formData.state}
                              onChange={handleInputChange}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                              City
                            </label>
                            <input
                              id="city"
                              name="city"
                              value={formData.city}
                              onChange={handleInputChange}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required
                            />
                          </div>
                          <div>
                            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
                              ZIP / Postal Code
                            </label>
                            <input
                              id="zip"
                              name="zip"
                              value={formData.zip}
                              onChange={handleInputChange}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Order Summary */}
                      <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h3 className="text-md font-medium text-gray-900 mb-4">
                          Order Summary
                        </h3>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between">
                            <span className="text-gray-600">{selectedPlan.title} ({formData.package_type === 'monthly' ? 'Monthly' : 'Yearly'})</span>
                            <span className="font-medium">{formData.package_type === 'monthly' ? selectedPlan.price.split(' / ')[0] : selectedPlan.yearly.split(' / ')[0].split(' (')[0]}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tax</span>
                            <span className="font-medium">$0.00</span>
                          </div>
                          <div className="h-px bg-gray-200 my-2"></div>
                          <div className="flex justify-between">
                            <span className="font-medium">Total</span>
                            <span className="font-bold text-lg">{formData.package_type === 'monthly' ? selectedPlan.price.split(' / ')[0] : selectedPlan.yearly.split(' / ')[0].split(' (')[0]}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center mb-4 text-sm text-gray-600">
                          <AlertCircle className="h-4 w-4 text-gray-400 mr-2" />
                          <span>You can cancel or change your subscription at any time from your dashboard.</span>
                        </div>
                        
                        <button
                          type="submit"
                          disabled={loader}
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                        >
                          {loader ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            isAccountSuspended ? 'Renew Subscription' : 'Complete Payment'
                          )}
                        </button>
                      </div>
                      
                      <p className="text-xs text-gray-500 text-center">
                        By completing this payment, you agree to our Terms of Service and Privacy Policy.
                      </p>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover />
    </div>
  );
};

export default AddCard;